import { AttioTeamMemberResponse, AttioTeamRecord, AttioTeamRecordQueryResponse } from '../attio.types';
import { BaseAttioService } from './BaseAttioService';

// Service-specific types
export interface AttioUserData {
  recordId: string;
  email: string;
  name: string;
}

export interface AttioUserSearchParams {
  email: string;
  limit?: number;
  offset?: number;
}

export class AttioUserService extends BaseAttioService {
  constructor() {
    super('AttioUserService');
  }
  /**
   * Get user record ID from email
   */
  async getUserRecordId(userEmail: string): Promise<string> {
    try {
      const body = {
        filter: {
          email: userEmail,
        },
        limit: 1,
        offset: 0,
      };

      const response = await this.post<AttioTeamRecordQueryResponse>(this.endpoints.retrieveTeamRecord, body);

      if (response.data && response.data.length > 0) {
        return response.data[0].id.record_id;
      }
      throw new Error('User not found');
    } catch (error) {
      console.error('Error fetching user record ID:', error);
      throw error;
    }
  }

  /**
   * Search users by email
   */
  async searchUsers({ email, limit = 10, offset = 0 }: AttioUserSearchParams): Promise<AttioUserData[]> {
    try {
      const body = {
        filter: {
          email,
        },
        limit,
        offset,
      };

      const response = await this.post<AttioTeamRecordQueryResponse>(this.endpoints.retrieveTeamRecord, body);

      if (response.data && response.data.length > 0) {
        return response.data.map((user: AttioTeamRecord) => ({
          recordId: user.id.record_id,
          email: user.values.email?.[0]?.value || '',
          name: user.values.name?.[0]?.value || '',
        }));
      }

      return [];
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  /**
   * Get user details by record ID
   */
  async getUserById(recordId: string): Promise<AttioUserData | null> {
    try {
      const { AttioTeamService } = await import('./AttioTeamService');
      const teamService = new AttioTeamService();
      const teamData = await teamService.getTeamMember(recordId);

      return {
        recordId,
        email: teamData.email || '',
        name: teamData.name,
      };
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      return null;
    }
  }

  /**
   * Get user record ID from email (legacy function from attio-common.ts)
   */
  async getUserRecordIdLegacy(userEmail: string): Promise<string> {
    return this.getUserRecordId(userEmail);
  }
}
